import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const ViewSchoolVisitHistoryModal = ({ closePopup, modalData }) => {
  const {
    partyId,
    schoolName,
    address,
    Area,
    contactPersons = [],
    visitCountByOwner = [],
    totalVisitCount = 0,
  } = modalData || {};

  const textPrimary = "#1f2937";
  const textSecondary = "#4b5563";

  return (
    <Box
      sx={{
        backgroundColor: "#f8fafc",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e5e7eb",
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: textPrimary,
            fontWeight: 700,
          }}
        >
          School Visit History
          <Typography
            component="span"
            sx={{
              ml: 1,
              color: textSecondary,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {schoolName} ({partyId})
          </Typography>
        </Typography>

        <IconButton onClick={closePopup}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box
        sx={{
          p: 3,
          overflowY: "auto",
          flex: 1,
        }}
      >
        {/* School Information */}
        <Card
          variant="outlined"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          <CardContent>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: textPrimary,
                mb: 1,
              }}
            >
              Address
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: textSecondary,
                fontWeight: 500,
                lineHeight: 1.7,
              }}
            >
              {address || "-"}
            </Typography>

            {Area && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: textPrimary,
                    mb: 1,
                  }}
                >
                  Area
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: textSecondary,
                    fontWeight: 500,
                  }}
                >
                  {Area}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        {/* Total Visits */}
        <Card
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          <CardContent
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: textPrimary,
              }}
            >
              Total Visits Yet Recorded
            </Typography>

            <Chip
              label={totalVisitCount}
              color={totalVisitCount > 0 ? "success" : "default"}
              sx={{
                fontWeight: 700,
                fontSize: 14,
              }}
            />
          </CardContent>
        </Card>

        {/* Visit Breakdown */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: textPrimary,
            mb: 2,
          }}
        >
          Visit Breakdown by Owner
        </Typography>

        {visitCountByOwner.length > 0 ? (
          <Table
            size="small"
            sx={{
              mb: 4,
              backgroundColor: "#fff",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1976d2" }}>
                <TableCell
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Employee ID
                </TableCell>

                <TableCell
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Owner Name
                </TableCell>

                <TableCell
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Visit Count
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visitCountByOwner.map((owner, index) => (
                <TableRow
                  key={owner.ownerId}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      color: textPrimary,
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    {owner.empId || "-"}
                  </TableCell>

                  <TableCell
                    sx={{
                      color: textPrimary,
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    {owner.ownerName || "-"}
                  </TableCell>

                  <TableCell
                    sx={{
                      color: "#1565c0",
                      fontWeight: 700,
                      textAlign: "center",
                    }}
                  >
                    {owner.visitCount ?? 0}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: textSecondary,
              mb: 3,
            }}
          >
            No visits recorded yet.
          </Typography>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Contact Persons */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: textPrimary,
            mb: 2,
          }}
        >
          Contact Persons
        </Typography>

        {contactPersons.length > 0 ? (
          <Table
            size="small"
            sx={{
              backgroundColor: "#fff",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1976d2" }}>
                <TableCell
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Name
                </TableCell>

                <TableCell
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Phone
                </TableCell>

                <TableCell
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Role
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {contactPersons.map((cp, index) => (
                <TableRow
                  key={cp.email || index}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      color: textPrimary,
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    {cp.contactPersonName || "-"}
                  </TableCell>

                  <TableCell
                    sx={{
                      color: textPrimary,
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    {cp.contactPersonNumber || "-"}
                  </TableCell>

                  <TableCell
                    sx={{
                      color: textSecondary,
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    {cp.contactPersonRole || "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: textSecondary,
            }}
          >
            No contact persons available.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ViewSchoolVisitHistoryModal;